import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Save } from '@mui/icons-material'
import { Button, DropdownSelect, Group, Layout, Link, Waves, media, spacing, styled } from '@pergas-common/pergas-components'
import { Add, Remove } from '@pergas-common/pergas-icons'
import Input, { Textarea } from '../../Input.js'
import Page from '../Page.js'
import { selectLocale } from '../../../redux/locale/selectors.js'
import useUrlQuery from '../../../hooks/useUrlQuery.js'
import Divider from '../../Divider.js'
import { DeleteItemV2 } from '../../dialogs/DeleteItem'
import { sortArray } from '../../../util.js'
import ColorList from '../../ColorList.js'
import { useAddTagMutation, useDeleteTagMutation, useGetTagByIdQuery, useUpdateTagMutation } from '../../../redux/tag/hooks.js'
import { TagIcon } from '../../icons.js'
import TagTypesList from '../../TagTypesList.js'

const DropdownHolder = styled.div`
  width: 100%;
  max-width: 320px;
  background: ${({ theme }) => theme.palette.common.primary};
`

const StyledForm = styled.form`
  display: flex;
  max-width: 800px;
  flex-direction: column;
  align-items: stretch;

  ${media.medium`
    flex-direction: row;
  `}

  > div:first-child {
    max-width: 320px;
    width: 100%;
  }
  > div:last-child {
    padding-left: ${spacing(4)};
  }
`

const Form = ({ id, onSubmit, data }) => {
  const ref = useRef(null)
  const { state } = useLocation()
  const locale = useSelector(selectLocale)
  const defaultTagType = {
    id: data?.tag_type_name || state?.tag_type_name || 'employee',
    name: data?.name || state?.name || locale.employee,
    color: data?.color || state?.color || '#0b3142'
  }

  const tagTypeTypes = sortArray([
    { id: 'contact', name: locale.contact },
    { id: 'department', name: locale.department },
    { id: 'project', name: locale.project },
    { id: 'category', name: locale.category },
    { id: 'contact_person', name: locale.contact_person },
    { id: 'employee', name: locale.employee },
    { id: 'ticket', name: locale.ticket }
  ], 'name')

  const [tagType, setTagType] = useState(defaultTagType)

  const submit = (e) => {
    e.preventDefault()
    const payload = {}
    const formData = new FormData(ref.current)
    for (const [key, value] of formData.entries()) {
      /* we are doing some magical things with input type color and radio buttons.
      This is required to force initial checked state to a color and not a boolean string
      */
      if (key === 'color') {
        payload.color = tagType.color
      } else {
        payload[key] = value
      }
      if (id) {
        payload.id = Number(id)
      }
    }
    payload.tag_type_name = tagType.id

    onSubmit(payload)
  }
  return (
    <StyledForm onSubmit={submit} id='tag' ref={ref}>
      <div>
        <Input name='name' label={locale.name} defaultValue={data?.name} />
        <Textarea name='description' label={locale.description} defaultValue={data?.description} />
        <Divider />
        <DropdownHolder>
          <DropdownSelect
            name='tag_type_name'
            disabled={id}
            buttonLabel={tagTypeTypes.find(({ id }) => tagType?.id === id).name}
            items={tagTypeTypes.map(({ name }) => name)}
            handleSelectedItemChange={(item) => {
              const selected = tagTypeTypes.find(({ name }) => name === item.selectedItem)
              setTagType({
                ...selected,
                color: '#0b3142'
              })
            }}
          />
        </DropdownHolder>
      </div>
      <div>
        <ColorList
          color={tagType.color} setColor={(color) => {
            setTagType((prev) => {
              return {
                ...prev,
                color
              }
            })
          }}
        />
      </div>
    </StyledForm>
  )
}

const TagDetails = () => {
  const [remove, setRemove] = useState(false)
  const navigate = useNavigate()
  const query = useUrlQuery()
  const id = query.get('id')
  const locale = useSelector(selectLocale)
  const { data, isUnitialized, isLoading, isSuccessful } = useGetTagByIdQuery(id)
  const { mutation: updateMutation, isLoading: isUpdating } = useUpdateTagMutation()
  const { metadata, mutation: createMutation, isLoading: isCreating, isSuccessful: isCreated } = useAddTagMutation()
  const { mutation: deleteMutation, isLoading: isDeleting, isSuccessful: isDeleted } = useDeleteTagMutation()

  const pageMetadata = {
    title: locale.tag,
    icon: <TagIcon width={20} height={20} />,
    link: (
      <Link onClickHandler={() => {
        navigate('/tags/new', {
          replace: true,
          state: {
            tag_type_id: data?.tag_type_id,
            tag_type_name: data?.tag_type_name
          }
        })
      }}
      ><Add width={20} height={20} color='#28afe0' />
      </Link>
    )
  }

  const submit = (payload) => {
    if (id) {
      updateMutation(payload)
      return
    }
    createMutation(payload)
  }

  useEffect(() => {
    if (metadata?.id) {
      navigate(`/tags/edit?id=${metadata?.id}`, { replace: true })
    }
  }, [metadata, isCreated])

  useEffect(() => {
    if (isDeleted) {
      navigate('/tags', { replace: true })
    }
  }, [isDeleted])

  return (
    <Page metadata={pageMetadata}>
      <Layout.Aside>
        <TagTypesList />
      </Layout.Aside>
      <Layout.Section>
        <Group.Button>
          <Button
            type='button' disabled={!id || data?.read_only} onClick={() => {
              setRemove(true)
            }}
          >
            {isDeleting ? <Waves width={20} height={20} /> : <Remove width={20} height={20} color='#FF5656' />}<span>{locale.delete}</span>
          </Button>
          <Button type='submit' form='tag'>{(isUpdating || isCreating) ? <Waves width={20} height={20} /> : <Save style={{ color: '#34b3e1', width: 20, height: 20 }} />}<span>{locale.save}</span></Button>
        </Group.Button>
        {(isSuccessful || isUnitialized) && (
          <Form id={id} data={data} onSubmit={submit} key={id} />
        )}
        {(isLoading && !isUnitialized) && (
          <Waves width={48} height={48} />
        )}
      </Layout.Section>
      {remove && <DeleteItemV2
        text={`${data?.name}`}
        isOpen={remove}
        onCloseRequest={() => {
          setRemove(false)
        }}
        onSubmit={() => {
          deleteMutation({ id })
          setRemove(false)
        }}
                 />}

    </Page>
  )
}

export default TagDetails
